import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Button, Tabs, Typography, Descriptions, Tag, Dropdown, Menu, Segmented, Form, Select, Space, Divider, Modal } from 'antd';
import {
    PlusCircleOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import NotesComponent from '../../../components/Forms/NotesComponent';
import moment from 'moment';
import timeAgo, { formatPriceIndian } from '../../../constants/functions';
import EditableFormBlock from '../../../components/Forms/EditableField';
import { MasterConfigs } from '../../../interfaces/entity';
import { constants } from '../../../constants/common';
import SmallProfileCard from '../../../components/ContactInfo/SmallProfileCard';
import Timeline from '../Timeline';
import Attachments from '../Attachments';
import MailsComponent from '../Mails/MailsComponent';
import { accountConfig } from '../Accounts/AccountDetails';
const { Title, Text } = Typography;

const InvoiceDetails: React.FC<any> = ({ invoice }) => {
    const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const [highlighted, setHighlighted] = useState<string | null>(null);
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState<'overview' | 'timeline'>('overview');

    const invoiceOwnerConfig: MasterConfigs = {
        api: () => '/invoiceJobs',
        getFormColumns: () => [
            {
                label: 'Invoice Owner',
                name: 'invoiceOwner',
                type: 'select',
                span: 24,
                commonProps: {
                    disabled: true,
                },
                render: (text) => {
                    return [text?.firstName, text?.lastName].filter(Boolean).join(' ');
                }
            },
            {
                label: 'Total Amount',
                name: 'totalBill',
                type: 'text',
                commonProps: {
                    disabled: true,
                },
                span: 24,
                render: (text) => {
                    return text ? `INR ${formatPriceIndian(text)}` : 'N/A';
                },
            },
            {
                label: 'Invoice Number',
                name: 'invoiceNumber',
                commonProps: {
                    disabled: true,
                },
                span: 24
            },
            {
                label: 'Invoice Date',
                name: 'invoiceDate',
                type: 'text',
                commonProps: {
                    disabled: true,
                },
                render: (text) => {
                    return text ? moment(new Date(text)).format('DD MMM YYYY') : 'N/A';
                },
                span: 24,
            },
            {
                width: 150,
                label: 'Due Date',
                name: 'dueDate',
                type: 'date',
                render: (text) => text ? moment(new Date(text)).format('DD MMM YYYY') : 'N/A'
            },
            {
                width: 150,
                label: 'Paid Date',
                name: 'invoicePaidAt',
                type: 'date',
                render: (text) => text ? moment(new Date(text)).format('DD MMM YYYY') : 'N/A'
            },
        ],
        combo: []
    }

    const invoiceConfig: MasterConfigs = {
        api: () => '/invoiceJobs',
        getFormColumns: () => [
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Invoice Information</Title>
            },
            {
                label: 'Invoice Owner',
                name: 'invoiceOwner',
                type: 'select',
                isCombo: true,
                span: 12,
                render: (text) => {
                    return [text?.firstName, text?.lastName].filter(Boolean).join(' ');
                }
            },
            {
                label: 'Total Amount',
                name: 'totalBill',
                type: 'text',
                span: 12,
                commonProps: {
                    disabled: true,
                },
                render: (text) => {
                    return text ? `INR ${formatPriceIndian(text)}` : 'N/A';
                },
            },
            {
                label: 'TDS Amount',
                name: 'tdsAmount',
                type: 'text',
                span: 12,
                commonProps: {
                    disabled: true,
                },
                render: (text) => {
                    return text ? `INR ${formatPriceIndian(text)}` : 'N/A';
                },
            },
            {
                label: 'GST Amount',
                name: 'gstAmount',
                type: 'text',
                span: 12,
                commonProps: {
                    disabled: true,
                },
                render: (text) => {
                    return text ? `INR ${formatPriceIndian(text)}` : 'N/A';
                },
            },
            {
                label: 'Invoice Date',
                name: 'invoiceDate',
                type: 'text',
                span: 12,
                commonProps: {
                    disabled: true,
                },
                render: (text) => {
                    return text ? moment(new Date(text)).format('DD MMM YYYY') : 'N/A';
                },
            },
            {
                width: 150,
                label: 'Due Date',
                name: 'dueDate',
                type: 'date',
                span: 12,
                render: (text) => text ? moment(new Date(text)).format('DD MMM YYYY') : 'N/A'
            },
            {
                width: 150,
                label: 'Paid Date',
                name: 'invoicePaidAt',
                type: 'date',
                span: 12,
                render: (text) => text ? moment(new Date(text)).format('DD MMM YYYY') : 'N/A'
            },
            {
                width: 150,
                label: 'Invoice Status',
                name: 'invoiceStatus',
                type: 'select',
                children: [
                    { name: 'Draft', value: 'draft' },
                    { name: 'Sent', value: 'sent' },
                    { name: 'Paid', value: 'paid' },
                    { name: 'Cancelled', value: 'cancelled' },
                    { name: 'Overdue', value: 'overdue' },
                    { name: 'Partially Paid', value: 'partially_paid' },
                    { name: 'Refunded', value: 'refunded' },
                    { name: 'Void', value: 'void' }
                ],
                span: 24,
                render: (text) => text ? text : 'N/A'
            },
            {
                label: 'Modified By',
                name: 'updatedBy',
                commonProps: {
                    disabled: true
                },
                type: 'text',
                render: (text, data) => {
                    return <div>
                        {[text?.firstName, text?.lastName].filter(Boolean).join(' ') || 'N/A'}
                        <br />
                        <Text type="secondary" className="text-xs">
                            {moment(data?.updatedAt).format('llll')}
                        </Text>
                    </div>
                },
                span: 12,
            },
            {
                label: 'Created By',
                name: 'createdBy',
                commonProps: {
                    disabled: true
                },
                type: 'text',
                render: (text, data) => {
                    return <Space direction="vertical" size={0}>
                        <div>
                            {[text?.firstName, text?.lastName].filter(Boolean).join(' ') || 'N/A'}
                            <br />
                            <Text type="secondary" className="text-xs">
                                {moment(data?.createdAt).format('llll')}
                            </Text>
                        </div>
                    </Space>
                },
                span: 12,
            },
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Partial Pay Information</Title>
            },
            {
                name: "partialPayments",
                type: "form",
                span: 24,
                form: [
                    {
                        label: "Amount",
                        name: "amount",
                        type: "text",
                        span: 8,
                        rules: [{ required: true, message: 'Please enter amount' }],
                    },
                    {
                        label: "Payment Date",
                        name: "date",
                        type: "date",
                        rules: [{ required: true, message: 'Enter payment date' }],
                        render: (text) => text ? <span>{moment(new Date(text)).format('DD MMM YYYY')}</span> : 'N/A',
                        span: 8,
                    },
                    {
                        label: "Note",
                        name: "note",
                        type: "textarea",
                        inputTextAreaProps: {
                            rows: 1,
                            placeholder: 'Enter note',
                        },
                        rules: [{ required: true, message: 'Enter note' }],
                        render: (text) => text,
                        span: 8,
                    },
                ]
            },
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Description Information</Title>
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textarea',
                span: 24,
                textAreaProps: {
                    autoSize: { minRows: 3, maxRows: 6 },
                }
            },
        ],
        combo: [
            {
                api: '/users',
                name: 'invoiceOwner',
                idKey: '_id',
                render: (text) => <SmallProfileCard
                    name={constants.GET_FULL_NAME(text)}
                    avatar={text?.avatar ? text?.avatar : null}
                    subTitle={null}
                />
            },
        ],
    }

    const sections = [
        { id: 'notes', title: 'Notes' },
        { id: 'attachments', title: 'Attachments' },
        { id: 'products', title: 'Products' },
        { id: 'open-activities', title: 'Open Activities' },
        { id: 'closed-activities', title: 'Closed Activities' },
        { id: 'invited-meetings', title: 'Invited Meetings' },
        { id: 'emails', title: 'Emails' },
        { id: 'campaigns', title: 'Campaigns' },
        { id: 'social', title: 'Social' },
    ];

    const scrollTo = (id: string) => {
        const section = sectionRefs.current[id];
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setHighlighted(id); // Highlight

            setTimeout(() => {
                setHighlighted(current => (current === id ? null : current));
            }, 1000 * 5);
        }
    };

    return (
        <div className="flex overflow-hidden">
            <div className="w-64 bg-white shadow-sm border-r  h-[calc(100vh-60px)] p-4 flex-shrink-0">
                <Text strong className="block mb-4">Related List</Text>
                <ul className="text-sm">
                    {sections.map(section => (
                        <li
                            key={section.id}
                            onClick={() => scrollTo(section.id)}
                            className="cursor-pointer hover:bg-gray-100 py-2 px-2 rounded-xm group"
                        >
                            <div className='flex items-center justify-between h-5'>
                                {section.title}
                                <Button type='text' className='hidden group-hover:block' icon={<PlusCircleOutlined className='pt-1' />} size="small" />
                            </div>
                        </li>
                    ))}

                    <li className="text-blue-600 cursor-pointer hover:bg-gray-100 py-2 px-2 rounded-sm">Add Related List</li>
                </ul>
                <Text strong className="block mt-6 mb-2 ">Links</Text>
                <p className="text-blue-600 cursor-pointer text-sm hover:bg-gray-100 py-2 px-2 rounded-sm">Add Link</p>
            </div>

            <div className="flex-1 overflow-y-auto h-full max-h-[calc(100vh-60px)] p-2">
                <div className="flex-1 p-2">
                    <div className='flex items-center justify-between mb-4 '>
                        <Segmented
                            options={[
                                { label: 'Overview', value: 'overview' },
                                { label: 'Timeline', value: 'timeline' },
                            ]}
                            value={activeTab}
                            onChange={value => setActiveTab(value as 'overview' | 'timeline')}
                        />
                        <div>
                            <Text className='text-sm text-gray-500'>Last Update : {timeAgo(invoice?.updatedAt)}</Text>
                        </div>
                    </div>

                    {activeTab === 'overview' && (<div className='space-y-4'>
                        <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <Form form={form} layout="vertical">
                                    <EditableFormBlock
                                        config={invoiceOwnerConfig}
                                        id={invoice?._id}
                                        form={form}
                                    />
                                </Form>
                            </div>
                        </div>

                        <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <Form form={form} layout="vertical">
                                    <EditableFormBlock
                                        config={invoiceConfig}
                                        id={invoice?._id}
                                        form={form}
                                    />
                                </Form>
                            </div>
                        </div>

                        {invoice?.billTo?.business && (
                            <div className="bg-white rounded-md mb-6">
                                <div className="flex justify-between items-center p-4">
                                    <Form form={form} layout="vertical">
                                        <EditableFormBlock
                                            config={accountConfig}
                                            id={invoice?.billTo?.business?._id}
                                            form={form}
                                        />
                                    </Form>
                                </div>
                            </div>
                        )}

                        {sections.map(section => (
                            <div
                                key={section.id}
                                ref={el => (sectionRefs.current[section.id] = el)}
                                className="bg-white rounded-md mb-6"
                            >
                                {!['Attachments'].includes(section.title) && <div className="flex justify-between items-center py-2 px-4 border-b">
                                    <Title level={5} className={highlighted === section.id ? 'bg-yellow-200 px-1' : ''}>{section.title}</Title>
                                    {section.title !== 'Notes' && <Button type="text" disabled>Add {section.title}</Button>}
                                </div>}
                                {!['Notes', 'Attachments'].includes(section.title) && <div className="p-4 text-gray-300 text-center">No {section.title.toLowerCase()} available</div>}
                                {section.title === 'Notes' && <NotesComponent refType={'InvoiceJobs'} refId={invoice?._id} />}
                                {section.title === 'Attachments' && <div className='p-4'><Attachments refType={'InvoiceJobs'} refId={invoice?._id} /> </div>}
                            </div>
                        ))}
                        <MailsComponent Email={invoice?.billTo?.contact?.email} sectionRefs={sectionRefs} highlighted={highlighted} />
                    </div>)}
                    {activeTab === 'timeline' && <Timeline type="InvoiceJobs" id={invoice?._id} />}
                </div>
            </div>
        </div>
    );
};

export default InvoiceDetails;